/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_markets-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_markets-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_markets-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, code
 * - Sortable fields: sort_order, created_at, updated_at
 * - Required fields: id, code, name, sort_order, archived, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_markets-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_markets-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_markets.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_markets-entity-config.ts
 * Public fields: 7/7 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupMarketsClient, LookupMarketsQueryOptions } from '../generated_clients/generated-lookup_markets-api-client.js';
import {
  LookupMarketsInsert,
  LookupMarketsUpdate,
  LookupMarketsFieldConfig
} from '../../types/generated_schemas/generated-lookup_markets-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_markets_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupMarketsKeys = {
  all: ['lookup_markets'] as const,
  lists: () => [...lookupMarketsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupMarketsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupMarketsKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupMarketsKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupMarketsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupMarketsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupMarketsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupMarketsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupMarketsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupMarketsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupMarketsClient();
};

/**
 * Hook to get all lookup_markets with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_markets data
 */
export const useLookupMarketsList = (options?: LookupMarketsQueryOptions) => {
  const client = useLookupMarketsClient();
  
  return useQuery({
    queryKey: lookupMarketsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_market by ID
 *
 * @param id - The lookup_market ID
 * @returns React Query result with single lookup_market data
 */
export const useLookupMarkets = (id: string) => {
  const client = useLookupMarketsClient();
  
  return useQuery({
    queryKey: lookupMarketsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_markets
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_markets_business_hooks/
 */

/**
 * Hook to create a new lookup_market
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, code, name, sort_order, archived, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_market
 */
export const useCreateLookupMarkets = () => {
  const client = useLookupMarketsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupMarketsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupMarketsKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_market
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_market
 */
export const useUpdateLookupMarkets = () => {
  const client = useLookupMarketsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupMarketsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupMarketsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupMarketsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_market
 *
 * @returns React Query mutation for deleting lookup_market
 */
export const useDeleteLookupMarkets = () => {
  const client = useLookupMarketsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupMarketsKeys.all });
    },
  });
};

/**
 * Hook to count lookup_markets with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, code
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupMarketsCount = (filter?: Partial<Record<typeof LookupMarketsFieldConfig.filterable[number], any>>) => {
  const client = useLookupMarketsClient();
  
  return useQuery({
    queryKey: lookupMarketsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupMarketsFieldConfig = () => {
  return LookupMarketsFieldConfig;
};